<template>
    <div component="loginCodeForm">
        <transition :name="state == 'enter-recipient' ? 'left' : 'right'">
            <form @submit.prevent="submitRecipient" v-if="state == 'enter-recipient'">
                <div class="input">
                    <input-tel
                        v-if="grant_type === 'sms_code'"
                        v-model="recipient"
                        @keydown.enter.prevent="onEnter"
                        @onValidate="onPhoneValidate"
                        :required="true"
                    ></input-tel>
                    <input
                        @keydown.enter.prevent="onEnter"
                        type="email"
                        name="loginEmail"
                        v-if="grant_type === 'email_code'"
                        v-model="recipient"
                        v-validate="'required|email'"
                        placeholder="your@email.com"
                    />
                    <label v-if="!recipientError">{{ getTitle }}</label>
                    <span class="error-message" v-if="recipientError">{{ recipientError }}</span>
                </div>
                <div class="buttons">
                    <btn
                        :icon="'navigate_before'"
                        size="big"
                        radius="big"
                        tpos="none"
                        color="ghost"
                        @click.prevent="$emit('setChannel', null)"
                    ></btn>
                    <btn
                        class="submit-button"
                        ref="submitBtnRecipient"
                        :state="getIsValid"
                        :icon="'navigate_next'"
                        :text="'Confirm'"
                        :size="'big'"
                        :radius="'big'"
                        tpos="left"
                    ></btn>
                </div>
            </form>
        </transition>
        <transition :name="state == 'enter-recipient' ? 'left' : 'right'">
            <form @submit.prevent="submitCode" v-if="state === 'enter-code'">
                <div class="input">
                    <input type="text" v-model="code" placeholder="XXXXX" @keydown.enter.prevent="onEnter" />
                    <label v-if="!codeError">Enter Code</label>
                    <span class="error-message" v-if="codeError">{{ codeError }}</span>
                </div>
                <div class="buttons">
                    <btn
                        @click.prevent="stateEnterRecipient"
                        :icon="'navigate_before'"
                        size="big"
                        radius="big"
                        tpos="none"
                        color="ghost"
                    ></btn>
                    <btn
                        class="submit-button"
                        ref="submitBtnCode"
                        :icon="'navigate_next'"
                        :text="'Confirm'"
                        :size="'big'"
                        :radius="'big'"
                        tpos="left"
                    ></btn>
                </div>
            </form>
        </transition>
    </div>
</template>
<script>
import { mapGetters, mapActions } from 'vuex';

import Messages from '@/messages.js';

export default {
    name: 'LoginCodeForm',

    props: {
        grant_type: {
            type: String,
            required: true,
        },

        icon: {
            type: String,
        },
    },

    data() {
        return {
            phoneIsValid: false,
            email_codeIsValid: false,
            state: 'enter-recipient',
            loading: false,

            recipient: '',
            code: '',

            recipientError: '',
            codeError: '',
        };
    },

    methods: {
        onEnter() {
            switch (this.state) {
                case 'enter-recipient':
                    this.submitRecipient();
                    break;
                case 'enter-code':
                    this.submitCode();
                    break;
            }
        },

        onPhoneValidate(response) {
            this.phoneIsValid = response.isValid;
        },

        reset() {
            this.stateEnterRecipient();
            this.recipient = '';
            this.code = '';
        },

        stateEnterRecipient() {
            this.state = 'enter-recipient';
        },

        stateEnterCode() {
            this.state = 'enter-code';
        },

        async submitRecipient() {
            if (!this.recipient.length || this.loading === true) {
                return false;
            }

            this.loading = true;
            this.$refs.submitBtnRecipient.setLoading();

            const grantToChannel = {
                sms_code: 'sms',
                email_code: 'email',
            };

            try {
                await this.requestPasswordlessLoginCode({
                    recipient: this.recipient.replace(' ', ''),
                    channel: grantToChannel[this.grant_type],
                });
                this.$refs.submitBtnRecipient.setSuccess('Sent');
                this.stateEnterCode();
                this.recipient = '';
            } catch (e) {
                this.$env.log.error(e.response);
                this.$refs.submitBtnRecipient.setError('Failed');
                this.setRecipientError(Messages(e.response.data.error));
            } finally {
                this.loading = false;
            }
        },

        async submitCode() {
            if (this.loading == true) return false;

            this.loading = true;
            this.$refs.submitBtnCode.setLoading();

            try {
                await this.$store.dispatch('login', {
                    grant_type: this.grant_type,
                    code: this.code,
                });
                this.$refs.submitBtnCode.setSuccess('Success');
                this.code = '';
                this.$emit('success');
            } catch (e) {
                this.$env.log.error(e);
                this.$refs.submitBtnCode.setError('Failed');
                this.setCodeError(Messages(e.response.data.error));
            } finally {
                this.loading = false;
            }
        },

        setRecipientError(msg) {
            this.recipientError = msg;
            setTimeout(() => {
                this.recipientError = '';
            }, 5000);
        },

        setCodeError(msg) {
            this.codeError = msg;
            setTimeout(() => {
                this.codeError = '';
            }, 5000);
        },

        ...mapActions(['requestPasswordlessLoginCode']),
    },

    computed: {
        getTitle() {
            switch (this.grant_type) {
                case 'sms_code':
                    return 'Enter phone';
                case 'email_code':
                    return 'Enter Email';
            }
        },

        isValid() {
            return Object.keys(this.fields).every((field) => {
                return this.fields[field] && this.fields[field].valid;
            });
        },

        getIsValid() {
            if (this.phoneIsValid && this.grant_type === 'sms_code') return '';
            if (this.fields.loginEmail && this.fields.loginEmail.valid && this.grant_type === 'email_code') return '';
            return 'disabled';
        },
    },
};
</script>
<style lang="less" scoped>
@import '~@variables';
@import '~vue-multiselect/dist/vue-multiselect.min.css';

[component='loginCodeForm'] {
    display: flex;
    flex-direction: column;
    width: 100%;

    form {
        display: flex;
        flex-direction: column;
        width: 100%;

        .input {
            width: 100%;
            text-align: center;

            label {
                top: 5px;
                display: block;
                text-transform: uppercase;
            }

            input {
                font-size: 16px;
                text-align: center;

                &::placeholder {
                    color: #ddd;
                }
            }

            .error-message {
                top: 5px;
                color: @accent;
                font-size: 11px;
                display: block;
                position: relative;
                text-transform: uppercase;
            }
        }

        .form-group {
            display: flex;
            position: relative;

            input {
                height: 37px;
                text-align: left;
                display: inline-block;
            }
        }
    }

    .text-link {
        margin: 5px 0;
        text-decoration: underline;
        cursor: pointer;
    }

    .buttons {
        .flex-center;
        justify-content: space-between;
        width: 100%;
        margin: 20px 0;
    }
}
</style>
